<template>
  <div>
    <el-dialog
      append-to-body
      :model-value="visible"
      :show-close="false"
      class="schedule-detail"
      destroy-on-close
      :z-index="1000"
      :close-on-click-modal="!cancelDialogVisible"
      @close="handleDialogClose"
    >
      <template #header>
        <div class="flex justify-between">
          <div class="flex items-center gap-2">
            <img src="@/assets/my-calendar/calendar/white-calendar.svg" alt="calendar" />
            <span data-testid="my-calendar.schedule-detail.title-text" class="header-font">{{ modalTitle }}</span>
          </div>
          <img
            data-testid="my-calendar.schedule-detail.close-btn"
            src="@/assets/my-calendar/white-close.svg"
            class="w-6 h-6 cursor-pointer"
            @click="$emit('close')" 
          />
        </div>
      </template>

      <div class="flex gap-3">
        <div class="border rounded-[2px] border-[#E6E6E6] w-[540px] bg-white px-4 py-3 h-[600px] overflow-y-auto">
          <TabSwitchPanel
            v-model="activeTab"
            :hide-switch-tab="hideSwitchTab"
            :disable-patient-selector="disablePatientSelector"
            @update:disabled="disableBtn = $event"
          />

        </div>
        <div class="flex-1">
          <ScheduleDetailCalendar v-if="visible" :active-tab="activeTab" :disable-btn="disableBtn"/>
        </div>
      </div>
  
      <template #footer>
        <div class="flex justify-between items-center gap-3 w-full">
          <ResponsiveButton
            data-testid="my-calendar.schedule-detail.cancel-btn"
            button_type="text"
            built_in_theme="dark"
            size="medium"
            display_name="Cancel"
            @click-button="$emit('close')"
          />
          <div class="flex justify-end items-center gap-3">
            <ResponsiveButton
              v-if="showCancelAppointmentBtn"
              data-testid="my-calendar.schedule-detail.cancel-appointment-btn"
              button_type="outlined"
              built_in_theme="error"
              size="medium"
              display_name="Cancel Appointment"
              :disabled="cancelLoading"
              @click-button="openCancelDialog"
            />
            <ResponsiveButton
              button_type="filled"
              built_in_theme="dark"
              size="medium"
              :display_name="submitLoading ? loadingBtnText : actionBtnText"
              :disabled="disableBtn || submitLoading"
              data-testid="my-calendar.schedule-detail.submit-btn"
              @click-button="createOrUpdateEvent"
            >
              <template v-if="submitLoading" #prefix>
                <span class="submit-spinner" aria-hidden="true"></span>
              </template>
            </ResponsiveButton>
          </div>
        </div>
      </template>
    </el-dialog>

    <CancelAppointmentDialog
      :visible="cancelDialogVisible"
      :loading="cancelLoading"
      :error="cancelError"
      @close="closeCancelDialog"
      @confirm="confirmCancelAppointment"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import moment from 'moment'
import { useStore } from 'vuex'
import TabSwitchPanel from './TabSwitchPanel.vue'
import ScheduleDetailCalendar from './ScheduleDetailCalendar.vue'
import CancelAppointmentDialog from './CancelAppointmentDialog.vue'


const store = useStore()
const props = defineProps({
  visible: Boolean,
  activeTab: {
    type: String,
    default: 'appointment',
  },
  hideSwitchTab: {
    type: Boolean,
    default: false,
  },
  disablePatientSelector: {
    type: Boolean,
    default: false,
  },
})


const triggerElement = ref(null)
const emit = defineEmits(['close', 'createTask', 'createEvent', 'updateTask', 'updateEvent'])
const activeTab = ref(props.activeTab)
const currentEventInfo = computed(() => store.state.calendar.currentEventInfo)
const currentTaskInfo = computed(() => store.state.calendar.currentTaskInfo)
const lastCreatedEventResponse = computed(() => store.state.calendar.lastCreatedEventResponse)
const lastCreatedTaskResponse = computed(() => store.state.calendar.lastCreatedTaskResponse)
const lastUpdatedTaskResponse = computed(() => store.state.calendar.lastUpdatedTaskResponse)
const lastUpdatedEventResponse = computed(() => store.state.calendar.lastUpdatedEventResponse)

const actionBtnText = computed(() => {
  const eventMode = currentEventInfo.value?.mode
  const taskMode = currentTaskInfo.value?.mode
  if (eventMode === 'edit' && activeTab.value === 'appointment') {
    return 'Update Appointment'
  }
  if (taskMode === 'edit' && activeTab.value === 'task') {
    return 'Update Task'
  }
  return activeTab.value === 'appointment' ? 'Create Appointment' : 'Create Task'
})

// Label shown while the create/update request is in flight
const loadingBtnText = computed(() => {
  const eventMode = currentEventInfo.value?.mode
  const taskMode = currentTaskInfo.value?.mode
  if (activeTab.value === 'appointment') {
    return eventMode === 'edit' ? 'Updating Appointment...' : 'Creating Appointment...'
  }
  return taskMode === 'edit' ? 'Updating Task...' : 'Creating Task...'
})

const modalTitle = computed(() => {
  const eventMode = currentEventInfo.value?.mode
  const taskMode = currentTaskInfo.value?.mode
  
  if (activeTab.value === 'task') {
    return taskMode === 'edit' ? 'Edit Task' : 'Create Task'
  } else {
    return eventMode === 'edit' ? 'Edit Appointment' : 'Create Appointment'
  }
})

const disableBtn = ref(true)
const submitLoading = ref(false)

const cancelLoading = ref(false)
const cancelDialogVisible = ref(false)
const cancelError = ref('')
const cancelEventId = ref(null)

const showCancelAppointmentBtn = computed(() => {
  const info = currentEventInfo.value
  // console.log(info, 'info')
  if (activeTab.value !== 'appointment') return false
  if (!info?.event_id) return false
  if (info?.mode !== 'edit') return false
  if (info?.is_canceled) return false
  return true
})


const createOrUpdateEvent = async () => {
  if (submitLoading.value) return

  // Clear previous responses before creating new one
  store.commit('calendar/SET_LAST_CREATED_EVENT_RESPONSE', null)
  store.commit('calendar/SET_LAST_CREATED_TASK_RESPONSE', null)
  store.commit('calendar/SET_LAST_UPDATED_TASK_RESPONSE', null)
  store.commit('calendar/SET_LAST_UPDATED_EVENT_RESPONSE', null)

  submitLoading.value = true
  
  await store.dispatch('calendar/setCreateOrUpdateEventAction', true)
}

const openCancelDialog = () => {
  if (!showCancelAppointmentBtn.value) return
  cancelEventId.value = currentEventInfo.value?.event_id || null
  cancelError.value = ''
  cancelDialogVisible.value = true
}

const closeCancelDialog = () => {
  cancelDialogVisible.value = false
  cancelError.value = ''
  cancelEventId.value = null
}

// Override el-dialog close behavior when cancel dialog is open
const handleDialogClose = () => {
  if (cancelDialogVisible.value) return
  emit('close');
}

const confirmCancelAppointment = async () => {
  if (!cancelEventId.value) return
  cancelLoading.value = true
  try {
    const res = await store.dispatch('calendar/deleteEvent', {
      event_id: cancelEventId.value,
    })
    if (!res?.success) {
      cancelError.value = res?.message || 'Failed to cancel appointment. Please try again.'
      return
    }

    store.dispatch('calendar/setCurrentEventInfo', null)
    store.dispatch('calendar/setPreScheduleInfo', {})

    closeCancelDialog()
    emit('close')

    const calendarId = store.state.calendar.creatorCalendarId
    if (calendarId) {
      const userInfo = store.state.login.userInfo;
      const isClinic150105 = userInfo?.clinic_id === 150105;
      const selectedClinicianCalendarIds = store.getters['calendar/selectedClinicianCalendarIds'] || []
      const calendarIds = isClinic150105 
        ? selectedClinicianCalendarIds
        : [calendarId];

      if (isClinic150105 && calendarIds.length === 0) {
        store.commit('calendar/SET_RAW_EVENT_LIST', [])
        return
      }
      
      try {
        store.commit('calendar/SET_CALENDAR_LOADING', true);
        await store.dispatch('calendar/fetchEvents', {
          calendar_ids: calendarIds,
          start_time: moment.utc('1970-01-01').startOf('day').toISOString(),
          end_time: moment().add(2, 'years').endOf('day').toISOString(),
        })
      } finally {
        store.commit('calendar/SET_CALENDAR_LOADING', false);
      }
    }
  } catch (error) {
    cancelError.value = 'Failed to cancel appointment. Please try again.'
  } finally {
    cancelLoading.value = false
  }
}

// Watch for task creation response and emit it
watch(lastCreatedTaskResponse, (newVal) => {
  if (newVal) {
    submitLoading.value = false
    emit('createTask', newVal)
    emit('close')
  }
})

// Watch for task update response and emit it
watch(lastUpdatedTaskResponse, (newVal) => {
  if (newVal) {
    submitLoading.value = false
    emit('updateTask', newVal)
    emit('close')
  }
})

// Watch for event update response and emit it
watch(lastUpdatedEventResponse, (newVal) => {
  if (newVal && newVal.event_id) {
    submitLoading.value = false
    emit('updateEvent', { event: newVal })
    
    // Use nextTick to ensure the event is emitted in the next tick
    nextTick(() => {
      emit('close')
      // Clear after emitting
      store.commit('calendar/SET_LAST_UPDATED_EVENT_RESPONSE', null)
    })
  }
})

// Watch for event creation response and emit it
watch(lastCreatedEventResponse, (newVal) => {
  if (newVal) {
    submitLoading.value = false
    emit('createEvent', newVal)
    emit('close')
  }
})

watch(() => store.state.calendar.createOrUpdateEventAction, (newVal) => {
  if (!newVal) {
    submitLoading.value = false

    // The action flag resets on both success and failure, so only auto-close the
    // reschedule modal when the submit actually went through.
    if (
      activeTab.value === 'appointment' &&
      currentEventInfo.value?.mode === 'reschedule' &&
      !store.state.calendar.lastSubmitFailed
    ) {
      emit('close')
    }
  }
})

watch(() => props.activeTab, (newVal) => {
  activeTab.value = newVal
})

// Handle Esc key press
const handleKeyDown = (event) => {
  if (event.key === 'Escape' || event.key === 'Esc') {
    // Don't close if cancel dialog is open
    if (cancelDialogVisible.value) return;
    emit('close');
  }
};

// Watch for visibility changes to manage keyboard listener and focus
watch(() => props.visible, (newVal) => {
  if (newVal) {
    // Store the trigger element for focus return
    triggerElement.value = document.activeElement;
    // Add keyboard event listener
    document.addEventListener('keydown', handleKeyDown);
  } else {
    // Remove keyboard event listener
    document.removeEventListener('keydown', handleKeyDown);
    // Return focus to trigger element
    if (triggerElement.value && triggerElement.value.focus) {
      nextTick(() => {
        triggerElement.value.focus();
      });
    }
  }
});

onUnmounted(() => {
  // Clean up event listener on unmount
  document.removeEventListener('keydown', handleKeyDown);
});
</script>

<style scoped>
/* Spinner shown in the submit button while the API call is in flight */
.submit-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: submit-spin 0.8s linear infinite;
}

@keyframes submit-spin {
  to { transform: rotate(360deg); }
}

.schedule-detail {
  position: fixed;
  left: 50%;
  width: 922px;
  margin: 0;
  z-index: 1000;
  transition: all 0.3s ease;
}

.header-font {
  font-weight: 600;
  font-size: 20px;
  font-family: 'Inter';
  line-height: 100%;
  letter-spacing: 0%;
  color: #FFFFFF;
}

</style>